'use client';

import { useState, useRef, useEffect, type MouseEvent } from 'react';

const DeleteIcon = (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
  </svg>
);

const CheckIcon = (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
);

/**
 * Always-visible, touch-friendly delete button with inline two-tap confirm.
 * First tap arms a red "Confirm?" state; second tap deletes. Auto-reverts after
 * 3s. Never hover-hidden, so it works on touch. `compact` (tight cards/rows)
 * shows only the check icon when armed, no label. `stopPropagation` on click so
 * it can live inside click-to-select rows without triggering the row.
 */
export default function DeleteButton({
  onDelete,
  compact = false,
  className = '',
}: {
  onDelete: () => void;
  compact?: boolean;
  className?: string;
}) {
  const [confirming, setConfirming] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  const handleClick = (e: MouseEvent) => {
    e.stopPropagation();
    if (!confirming) {
      setConfirming(true);
      timer.current = setTimeout(() => setConfirming(false), 3000);
      return;
    }
    if (timer.current) clearTimeout(timer.current);
    setConfirming(false);
    onDelete();
  };

  return (
    <button
      onClick={handleClick}
      className={`shrink-0 flex items-center gap-1 rounded p-1 text-[11px] font-medium transition-colors ${
        confirming ? 'text-red-400 bg-red-500/10' : 'text-zinc-500 hover:text-red-400'
      } ${className}`}
      title={confirming ? 'Tap again to confirm' : 'Remove'}
      aria-label={confirming ? 'Confirm remove' : 'Remove'}
    >
      {confirming ? CheckIcon : DeleteIcon}
      {confirming && !compact && <span className="pr-0.5">Confirm?</span>}
    </button>
  );
}
